"use client";

import useSWR from "swr";
import { useRef } from "react";

export type ProductType =
  | "cooker-hoods"
  | "dishwashers"
  | "freezers"
  | "fridge-freezers"
  | "fridges"
  | "ovens"
  | "tumble-dryers"
  | "washer-dryers"
  | "washing-machines";

export type YourekoMedal =
  | "Best"
  | "Gold"
  | "Silver"
  | "Bronze"
  | "Good value"
  | "None"
  | "N/A"
  | string;

export interface YourekoResult {
  productFound: boolean;
  canButtonBeShown: boolean;
  energyCostSavingUnformatted: number;
  numberOfAlternatives: number;
  medalRatingText: YourekoMedal;
}

export interface YourekoResponse {
  status?: { code: number; message: string; information?: string };
  results?: Record<string, YourekoResult>;
}

const SELLING_TO_API_MAP = {
  "Fridge Freezers": "fridge-freezers",
  Freezers: "freezers",
  Fridges: "fridges",
  "Washing Machines": "washing-machines",
  "Washer Dryers": "washer-dryers",
  "Tumble Dryers": "tumble-dryers",
  Dishwashers: "dishwashers",
  "Cooker Hoods": "cooker-hoods",
} as const satisfies Record<string, ProductType>;

type SellingProductType = keyof typeof SELLING_TO_API_MAP;

function isSellingProductType(v: string): v is SellingProductType {
  return Object.prototype.hasOwnProperty.call(SELLING_TO_API_MAP, v);
}

export const getProductType = (
  sellingProductType?: string,
): ProductType | undefined => {
  if (!sellingProductType || !isSellingProductType(sellingProductType)) return;
  return SELLING_TO_API_MAP[sellingProductType];
};

const cyclical: ProductType[] = [
  "dishwashers",
  "ovens",
  "tumble-dryers",
  "washer-dryers",
  "washing-machines",
];
const nonCyclical: ProductType[] = ["cooker-hoods"];

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return;
  const row = document.cookie.split("; ").find((r) => r.startsWith(`${name}=`));
  return row?.slice(name.length + 1);
}

function buildQuery(
  params: Record<string, string | string[] | number | boolean | undefined>,
) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null) continue;
    if (Array.isArray(v)) v.forEach((val) => qs.append(k, String(val)));
    else qs.append(k, String(v));
  }
  return qs.toString();
}

function cookiesToParams(productType: ProductType) {
  const params: Record<string, string[] | string | undefined> = {};

  const usage = getCookie(`youreko-${productType}_gb`);
  if (usage) {
    if (cyclical.includes(productType)) {
      params["customCyclesPerYear[]"] = [`${productType},${usage}`];
    } else if (nonCyclical.includes(productType)) {
      params["customMinutesOnPerYear[]"] = [`${productType},${usage}`];
    }
  }
  const years = getCookie(`youreko-${productType}_years-gb`);
  if (years) {
    params["customEnergyCostYears[]"] = [`${productType},${years}`];
  }
  const tariff = getCookie(`yourekoCustomTariff-gb`);
  if (tariff) {
    params["customTariff"] = tariff;
  }

  return params;
}

export interface UseYourekoLabelArgs {
  sellingProductType: string | undefined;
  variantTitle: string;
}

const ENDPOINT =
  "https://api.youreko.com/v1/en-gb/john-lewis/energy-labels.json";
const MINIMUM_DATA = true;

export function useYourekoLabels({
  sellingProductType,
  variantTitle,
}: UseYourekoLabelArgs) {
  const trimmed = sellingProductType?.trim() ?? "";
  const productType = getProductType(trimmed);

  const isDisabled = !productType;
  const disabledReason = isDisabled
    ? trimmed === ""
      ? "missingSellingProductType"
      : "unmappedSellingProductType"
    : undefined;

  const key = isDisabled
    ? null
    : (["youreko", "en-gb", "john-lewis", productType, variantTitle] as const);

  const makeQuery = (pt: ProductType, title: string) =>
    buildQuery({
      minimumData: MINIMUM_DATA,
      "productTypes[]": pt,
      productTitles: title,
      ...cookiesToParams(pt),
    });

  const lastQueryRef = useRef<string | null>(null);

  const fetcher = async ([_ns, _region, _partner, pt, title]: Readonly<
    [string, string, string, ProductType, string]
  >) => {
    const q = makeQuery(pt, title);
    const res = await fetch(`${ENDPOINT}?${q}`, { credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = (await res.json()) as YourekoResponse;

    lastQueryRef.current = q;
    return json;
  };

  const swr = useSWR<YourekoResponse>(key, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const result: YourekoResult | null = swr.data?.results
    ? (Object.values(swr.data.results)[0] ?? null)
    : null;

  const refetch = () => {
    if (isDisabled || !productType) return Promise.resolve(undefined);
    const current = makeQuery(productType, variantTitle);
    if (current === lastQueryRef.current) {
      return Promise.resolve(undefined);
    }
    return swr.mutate();
  };

  const currentUrl = () => {
    if (isDisabled || !productType) return null;
    return `${ENDPOINT}?${makeQuery(productType, variantTitle)}`;
  };

  return { ...swr, result, refetch, currentUrl, isDisabled, disabledReason };
}
